import React, { useState, useEffect, Suspense } from 'react';
import { Layout, Spin } from 'antd';
import { useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import { ProtectedRoute } from '../ProtectedRoute';
import AdminRoute from '../auth/AdminRoute';

const { Content } = Layout;

// Component to redirect from root to dashboard
export const RootRedirect = () => {
  return <Navigate to="/dashboard" replace />;
};

// Component to handle catch-all redirects
export const CatchAllRedirect = () => {
  return <Navigate to="/dashboard" replace />;
};

// Layout wrapper component for data router with maintenance mode check
export const AppLayoutWrapper = ({ isMaintenanceMode }) => {
  const location = useLocation();
  const { user, loading } = useSelector((state) => state.auth);
  const searchParams = new URLSearchParams(location.search);
  const urlBypassToken = searchParams.get('bypassToken');

  // Bypass token is handled via httpOnly cookie by the server — URL param is only for initial navigation
  if (isMaintenanceMode && !urlBypassToken) {
    return <Navigate to="/login" replace />;
  }

  if (!loading && user?.role === 'admin') {
    return <Navigate to="/admin/invoice-monitoring" replace />;
  }

  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
};

// Admin layout wrapper with maintenance mode check
export const AdminLayoutWrapper = ({ isMaintenanceMode }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlBypassToken = searchParams.get('bypassToken');

  // Bypass token is handled via httpOnly cookie by the server — URL param is only for initial navigation
  if (isMaintenanceMode && !urlBypassToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AdminRoute>
      <AppLayout />
    </AdminRoute>
  );
};

function AppLayout() {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobile = screenWidth < 768;
  const shouldCollapse = screenWidth < 1024;
  const [collapsed, setCollapsed] = useState(shouldCollapse);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newIsMobile = newWidth < 768;
      setScreenWidth(newWidth);

      if (newIsMobile && !isMobile && !collapsed) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);

    if (isMobile) {
      if (!collapsed) {
        document.body.classList.add('sidebar-open');
      } else {
        document.body.classList.remove('sidebar-open');
      }
    } else {
      document.body.classList.remove('sidebar-open');
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('sidebar-open');
    };
  }, [screenWidth, collapsed, isMobile]);

  const location = useLocation();
  const navigate = useNavigate();

  // Route to page key mapping - defined inline for AppLayout
  const routeToPageMap = {
    '/': 'dashboard',
    '/dashboard': 'dashboard',
    '/invoices': 'all-invoices',
    '/invoices/create': 'create-invoice',
    '/invoices/pending': 'pending-invoices',
    '/invoices/submitted': 'submitted-invoices',
    '/invoices/submitted/:uuid': 'submitted-document-details',
    '/users': 'users',
    '/audit': 'audit-logs',
    '/settings': 'settings',
    '/notifications/:id': 'notification-details'
  };

  // Get current page key based on URL path
  const getCurrentPage = () => {
    if (location.pathname.startsWith('/invoices/submitted/')) {
      return 'submitted-document-details';
    }
    return routeToPageMap[location.pathname] || 'dashboard';
  };

  // Get page title based on current selection
  const getPageTitle = () => {
    const titles = {
      dashboard: 'Dashboard',
      'all-invoices': 'All Invoices',
      'create-invoice': 'Create Invoice',
      'pending-invoices': 'Pending Invoices',
      'submitted-invoices': 'Submitted Invoices',
      'users': 'User Management',
      'audit-logs': 'Audit Logs',
      'settings': 'System Settings',
      'submitted-document-details': 'Submitted Document Details'
    };
    if (location.pathname.startsWith('/invoices/submitted/')) {
      return 'Submitted Document Details';
    }

    return titles[getCurrentPage()] || 'Dashboard';
  };

  const handlePageChange = (pageKey) => {
    const routes = Object.entries(routeToPageMap);
    const route = routes.find(([, key]) => key === pageKey);

    if (route) {
      navigate(route[0]);
    }
  };

  return (
    <Layout
      style={{ minHeight: '100vh' }}
      className={collapsed ? 'sidebar-collapsed' : ''}
    >
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        currentPage={getCurrentPage()}
        setCurrentPage={handlePageChange}
        isMobile={isMobile}
      />

      {isMobile && !collapsed && (
        <div
          className="sidebar-overlay"
          onClick={() => setCollapsed(true)}
        ></div>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 208), transition: 'all 0.2s' }}>
        <AppHeader
          getPageTitle={getPageTitle}
          currentPage={getCurrentPage()}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isMobile={isMobile}
        />

        <Content className="main-content-wrapper">
          <div className="page-content">
            <Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin size="large" />
              </div>
            }>
              <Outlet />
            </Suspense>
          </div>
        </Content>

        <AppFooter />
      </Layout>
    </Layout>
  );
}

export default AppLayout;
